import { PERMISSIONS_KEY } from '../constants'

const decodeJwt = (token: string): Record<string, string[]> | null => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/')
    const jsonPayload = decodeURIComponent(
      globalThis
        .atob(base64)
        .split('')
        .map((char: string) => {
          const codePoint = char.codePointAt(0)?.toString(16)
          const paddedCode = `00${codePoint}`.slice(-2)
          return `%${paddedCode}`
        })
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

// Derive the user's permissions from the `permission` claim of an OIDC access
// token. Returns [] for a missing/malformed token or absent claim.
export const derivePermissionsFromToken = (accessToken?: string): string[] => {
  if (!accessToken) {
    return []
  }

  const tokenClaims = decodeJwt(accessToken)
  if (!tokenClaims) {
    return []
  }

  return (tokenClaims.permission ?? []).map((p: string) =>
    p.replace(/\.\*$/, '')
  )
}

export const hasPermission = (
  permissions: string[],
  requiredPermissions: string | string[]
) => {
  if (typeof requiredPermissions === 'string') {
    return permissions?.some(permission => permission === requiredPermissions)
  }

  if (requiredPermissions.length === 0) {
    return false
  }

  return requiredPermissions.some(requiredPermission =>
    permissions?.some(permission => permission === requiredPermission)
  )
}

export const hasRolePermissions = (
  rolePermissions: string[],
  permissionName: string
) => {
  return (
    rolePermissions?.some(permission => permission === permissionName) ?? false
  )
}

export const getUserPermissions = () => {
  const permissions = sessionStorage.getItem(PERMISSIONS_KEY)

  if (!permissions) {
    return []
  }

  return JSON.parse(permissions)
}

export const hasRequiredPermission = (
  requiredPermissions: string | string[],
  permissions?: string[]
): boolean => {
  const sessionPermissions = getUserPermissions()
  const finalPermissions = permissions ?? sessionPermissions

  return hasPermission(finalPermissions, requiredPermissions)
}
